import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import mapValues from "lodash/mapValues";
import React, { useCallback, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import useValidationSync from "@/lib/hook/useValidationSync";
import { ApplicationFormPrepared } from "@/lib/prisma";

import useApplicationFormValidationSchema from "@/core/client/application/applicationForm/config/useApplicationFormValidationSchema";

import {
  ImmigramFormElementType,
  ImmigramFormInputElementConfig,
} from "./elements/common";
import { ImmigramFormBoundary } from "./ImmigramFormBoundary";
import { ImmigramFormElement } from "./ImmigramFormElement";

interface ImmigramFormProps {
  immigramFormConfig: any[];
  formData?: ApplicationFormPrepared | null;
  onFieldChange: (
    elementConfig: ImmigramFormInputElementConfig,
    value: string,
    formState: Record<string, string>
  ) => void;
  onIsValidChange?: (isValid: boolean) => void;
  disabled: boolean;
  withComments?: boolean;
  className?: string;
  showValidationErrors?: boolean;
  hiddenFieldNames?: string[];
}

function ImmigramForm({
  immigramFormConfig,
  onFieldChange,
  onIsValidChange,
  withComments,
  formData,
  disabled,
  className,
  showValidationErrors,
  hiddenFieldNames,
}: ImmigramFormProps) {
  const configDefinedValues = immigramFormConfig.reduce(
    (acc, elementConfig) => {
      if (
        elementConfig?.type &&
        Object.values(ImmigramFormElementType).includes(elementConfig.type) &&
        elementConfig.value
      ) {
        acc[elementConfig.name] = elementConfig.value;
      }

      return acc;
    },
    {}
  );

  const { control, getValues } = useForm<Record<string, string>>({
    defaultValues: {
      ...configDefinedValues,
      ...mapValues(formData?.fieldsMap || {}, (field) => field.value),
    },
  });

  const formValues = useWatch({ control });

  const validationSchema =
    useApplicationFormValidationSchema(immigramFormConfig);

  const formValidationErrors: any = useValidationSync(
    validationSchema,
    formValues
  );

  const isFormValidRef = useRef<boolean | undefined>();

  useEffect(() => {
    if (isFormValidRef.current !== !formValidationErrors) {
      onIsValidChange?.(!formValidationErrors);
      isFormValidRef.current = !formValidationErrors;
    }
  }, [formValidationErrors, isFormValidRef, onIsValidChange]);

  const handleFieldChange = useCallback(
    (elementConfig: ImmigramFormInputElementConfig, value: string) => {
      onFieldChange(elementConfig, value, getValues());
    },
    [onFieldChange, getValues]
  );

  return (
    <ImmigramFormBoundary name="form">
      <div className={clsxm("grid grid-cols-2 gap-x-5", className)}>
        {immigramFormConfig.map((elementConfig, i) => {
          if (
            hiddenFieldNames &&
            elementConfig?.name &&
            hiddenFieldNames.includes(elementConfig.name)
          ) {
            return null;
          }

          return (
            <div
              key={[elementConfig.type, elementConfig.name, i].join("-")}
              className={clsxm("mb-5", "col-span-2", elementConfig.className)}
            >
              <ImmigramFormBoundary name="form element">
                <ImmigramFormElement
                  withComments={withComments}
                  commentThreadId={
                    withComments
                      ? [
                          formData?.fieldsMap?.[elementConfig.name]?.id,
                          formData?.fieldsMap?.[elementConfig.name]?.name,
                        ].join("_")
                      : undefined
                  }
                  validationErrors={
                    showValidationErrors
                      ? formValidationErrors?.[elementConfig.name]
                      : undefined
                  }
                  elementConfig={elementConfig}
                  control={control}
                  onChange={handleFieldChange}
                  disabled={disabled}
                />
              </ImmigramFormBoundary>
            </div>
          );
        })}
      </div>
    </ImmigramFormBoundary>
  );
}

function ImmigramFormWithBoundary(props: ImmigramFormProps) {
  return (
    <ImmigramFormBoundary name="form">
      <ImmigramForm {...props} />
    </ImmigramFormBoundary>
  );
}
export { ImmigramFormWithBoundary as ImmigramForm };
